import { useState, useRef, useEffect } from 'react';

interface ImageCarouselProps {
  images: { url: string; label: string }[];
  className?: string;
  accentColor?: 'cyan' | 'gray';
}

export default function ImageCarousel({ images, className = '', accentColor = 'cyan' }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = 'unset';
  };

  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        closeFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  if (images.length === 0) return null;

  const indicatorActiveClass = accentColor === 'cyan' ? 'bg-cyan-400' : 'bg-gray-300';

  return (
    <>
      {/* Main Carousel */}
      <div className={`relative w-full ${className}`}>
        <div 
          ref={containerRef}
          className="relative w-full h-full rounded-2xl overflow-hidden cursor-pointer"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={openFullscreen}
        >
          {/* Images Container - Swipeable */}
          <div 
            className="flex transition-transform duration-300 ease-out h-full"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {images.map((image, index) => (
              <div key={index} className="w-full h-full flex-shrink-0">
                <img
                  src={image.url}
                  alt={image.label}
                  className="w-full h-full object-contain bg-black/20"
                />
              </div>
            ))}
          </div>
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
          
          {/* Image Label */}
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full pointer-events-none z-10">
            <span className="text-white text-sm font-medium">{images[currentIndex]?.label}</span>
          </div>

          {/* Fullscreen Hint */}
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-full hidden sm:flex items-center gap-2 pointer-events-none z-10">
            <i className="ri-fullscreen-line text-white text-sm"></i>
            <span className="text-white text-xs">Click to enlarge</span>
          </div>

          {/* Left Navigation Button - Always visible, large tap area */}
          <button
            onClick={prevImage}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 backdrop-blur-sm hover:bg-black/80 rounded-full flex items-center justify-center transition-all cursor-pointer z-20 pointer-events-auto border-0 outline-none"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl text-white"></i>
          </button>

          {/* Right Navigation Button - Always visible, large tap area */}
          <button
            onClick={nextImage}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 backdrop-blur-sm hover:bg-black/80 rounded-full flex items-center justify-center transition-all cursor-pointer z-20 pointer-events-auto border-0 outline-none"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl text-white"></i>
          </button>
        </div>

        {/* Carousel Indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                currentIndex === index ? `${indicatorActiveClass} w-8` : 'bg-gray-600 w-2'
              }`}
            />
          ))}
        </div>

        {/* Swipe Hint - Mobile only */}
        <div className="flex md:hidden justify-center mt-2">
          <span className="text-gray-500 text-xs flex items-center gap-1">
            <i className="ri-hand-coin-line"></i>
            Swipe or tap arrows to browse
          </span>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
          onClick={closeFullscreen}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close Button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 z-40 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all cursor-pointer border-0 outline-none"
            aria-label="Close fullscreen"
          >
            <i className="ri-close-line text-2xl text-white"></i>
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 z-40 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full pointer-events-none">
            <span className="text-white text-sm">{currentIndex + 1} / {images.length}</span>
          </div>

          {/* Image Label */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-40 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full pointer-events-none">
            <span className="text-white text-sm font-medium">{images[currentIndex]?.label}</span>
          </div>

          {/* Fullscreen Image */}
          <div 
            className="w-full h-full flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {images.map((image, index) => (
              <div key={index} className="w-full h-full flex-shrink-0 flex items-center justify-center p-4">
                <img
                  src={image.url}
                  alt={image.label}
                  className="max-w-full max-h-full object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ))}
          </div>

          {/* Left Navigation Button - Fullscreen */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(e); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all cursor-pointer z-40 pointer-events-auto border-0 outline-none"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-2xl md:text-3xl text-white"></i>
          </button>

          {/* Right Navigation Button - Fullscreen */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(e); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all cursor-pointer z-40 pointer-events-auto border-0 outline-none"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-2xl md:text-3xl text-white"></i>
          </button>

          {/* Bottom Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-40">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  currentIndex === index ? 'bg-white w-8' : 'bg-white/40 w-2'
                }`}
              />
            ))}
          </div>

          {/* Mobile Hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 md:hidden z-40 pointer-events-none">
            <span className="text-white/50 text-xs">Tap anywhere to close</span>
          </div>
        </div>
      )}
    </>
  );
}